import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["scrollable"]

  connect() {
    this.boundCheckScroll = this.checkScroll.bind(this)

    this._onPointerDown = this.onPointerDown.bind(this)
    this._onPointerMove = this.onPointerMove.bind(this)
    this._onPointerUp = this.onPointerUp.bind(this)
    this._onClickCapture = this.onClickCapture.bind(this)

    this.isPointerDown = false
    this.isDragging = false
    this.suppressNextClick = false
    this.pointerCaptured = false

    this.startY = 0
    this.startScrollTop = 0
    this.pointerId = null

    this.dragThresholdPx = 6

    this.checkScroll()
    this.scrollableTarget.addEventListener('scroll', this.boundCheckScroll)
    window.addEventListener('resize', this.boundCheckScroll)
    this.element.addEventListener('turbo:frame-load', this.boundCheckScroll)

    this.scrollableTarget.addEventListener('pointerdown', this._onPointerDown, { passive: true })
    window.addEventListener('pointermove', this._onPointerMove, { passive: false })
    window.addEventListener('pointerup', this._onPointerUp, { passive: true })
    window.addEventListener('pointercancel', this._onPointerUp, { passive: true })

    // Prevent native browser drag-and-drop on <a> overlays from cancelling pointermove
    this._onDragStart = (e) => e.preventDefault()
    this.scrollableTarget.addEventListener('dragstart', this._onDragStart)

    // Block click only after a drag
    this.scrollableTarget.addEventListener('click', this._onClickCapture, true)
  }

  disconnect() {
    this.scrollableTarget.removeEventListener('scroll', this.boundCheckScroll)
    window.removeEventListener('resize', this.boundCheckScroll)
    this.element.removeEventListener('turbo:frame-load', this.boundCheckScroll)

    this.scrollableTarget.removeEventListener('pointerdown', this._onPointerDown)
    window.removeEventListener('pointermove', this._onPointerMove)
    window.removeEventListener('pointerup', this._onPointerUp)
    window.removeEventListener('pointercancel', this._onPointerUp)

    this.scrollableTarget.removeEventListener('dragstart', this._onDragStart)

    this.scrollableTarget.removeEventListener('click', this._onClickCapture, true)
  }

  checkScroll() {
    const element = this.scrollableTarget
    const hasScroll = element.scrollHeight > element.clientHeight
    const isScrolledToBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 10
    const isScrolledFromTop = element.scrollTop > 10

    // Bottom shadow - on main container
    if (hasScroll && !isScrolledToBottom) {
      this.element.setAttribute('data-has-scroll', 'true')
    } else {
      this.element.setAttribute('data-has-scroll', 'false')
    }

    // Top shadow - on scrollable element
    if (hasScroll && isScrolledFromTop) {
      element.setAttribute('data-has-scroll-top', 'true')
    } else {
      element.setAttribute('data-has-scroll-top', 'false')
    }
  }

  onPointerDown(e) {
    if (e.button != null && e.button !== 0) return
    if (e.target?.closest?.('[data-no-drag]')) return

    this.isPointerDown = true
    this.isDragging = false
    this.suppressNextClick = false
    this.pointerCaptured = false

    this.pointerId = e.pointerId
    this.startY = e.clientY
    this.startScrollTop = this.scrollableTarget.scrollTop
  }

  onPointerMove(e) {
    if (!this.isPointerDown) return
    if (this.pointerId != null && e.pointerId !== this.pointerId) return

    const dy = e.clientY - this.startY
    const absDy = Math.abs(dy)

    // Start dragging only after threshold
    if (!this.isDragging && absDy >= this.dragThresholdPx) {
      this.isDragging = true
      this.suppressNextClick = true

      // Capture pointer ONLY when dragging starts (important!)
      if (!this.pointerCaptured) {
        try {
          this.scrollableTarget.setPointerCapture(e.pointerId)
          this.pointerCaptured = true
        } catch (_) {}
      }
    }

    if (!this.isDragging) return

    e.preventDefault()
    this.scrollableTarget.scrollTop = this.startScrollTop - dy
  }

  onPointerUp(e) {
    if (!this.isPointerDown) return
    if (this.pointerId != null && e.pointerId !== this.pointerId) return

    this.isPointerDown = false

    if (this.pointerCaptured) {
      try {
        this.scrollableTarget.releasePointerCapture(e.pointerId)
      } catch (_) {}
      this.pointerCaptured = false
    }

    // If it wasn't a drag, allow normal clicks
    if (!this.isDragging) {
      this.suppressNextClick = false
    }

    this.isDragging = false
    this.pointerId = null
  }

  onClickCapture(e) {
    if (!this.suppressNextClick) return

    e.preventDefault()
    e.stopPropagation()

    // Suppress only one click after drag
    this.suppressNextClick = false
  }
}
